import { EventEmitter } from '@apia/util';
import {
  Children,
  ReactElement,
  ReactNode,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import { useSubscription } from '@apia/util';
import { Box, BoxProps } from '@apia/theme';
import { TSortableListItem } from './SortableListItem';

type THover = { id: string; after: boolean };

function getTargetItem(ev: Event) {
  return (ev.target as HTMLElement).closest(
    '.sortableList__item',
  ) as HTMLElement;
}

export class SortableListHandler {
  #emitter = new EventEmitter<{ hover: THover; sort: null }>();

  #items: ReactElement<{ id: string }>[] = [];

  updateChildren = (items: ReactNode) => {
    this.#items = Children.toArray(items).filter((el): el is ReactElement =>
      isValidElement(el),
    );
  };

  #moveItem = (movingId: string, idBefore: string, after: boolean) => {
    if (movingId === idBefore) return;

    const itemIndex = this.#items.findIndex(
      (search) => search.props.id === movingId,
    );
    const item = this.#items.splice(itemIndex, 1)![0]!;
    const insertIndex = this.#items.findIndex(
      (search) => search.props.id === idBefore,
    );

    this.#items.splice(insertIndex + (after ? 1 : 0), 0, item);
    this.#emitter.emit('sort', null);
  };

  useItemEvents = () => {
    const unsuscribe = useRef(() => {});
    const ref = useCallback((el: HTMLElement) => {
      unsuscribe.current();

      if (el) {
        const handleDragEnd = (ev: DragEvent) => {
          getTargetItem(ev).classList.remove('dragging');
        };

        const handleDrop = (ev: DragEvent) => {
          const target = getTargetItem(ev);

          this.#moveItem(
            ev.dataTransfer!.getData('text'),
            target.id,
            target.classList.contains('after'),
          );

          target.classList.remove('dragover');
          target.classList.remove('after');
        };

        const handleDragLeave = (ev: DragEvent) => {
          getTargetItem(ev).classList.remove('dragover');
        };

        const handleDragOver = (ev: DragEvent) => {
          ev.preventDefault();
          // eslint-disable-next-line no-param-reassign
          ev.dataTransfer!.dropEffect = 'move';
          getTargetItem(ev).classList.add('dragover');

          const bounding = getTargetItem(ev).getBoundingClientRect();
          if (ev.clientY - bounding.top > bounding.height / 2)
            getTargetItem(ev).classList.add('after');
          else getTargetItem(ev).classList.remove('after');
        };

        document.addEventListener('dragend', handleDragEnd);
        el.addEventListener('drop', handleDrop);
        el.addEventListener('dragleave', handleDragLeave);
        el.addEventListener('dragover', handleDragOver);

        unsuscribe.current = () => {
          document.removeEventListener('dragend', handleDragEnd);
          el.removeEventListener('drop', handleDrop);
          el.removeEventListener('dragleave', handleDragLeave);
          el.removeEventListener('dragover', handleDragOver);
        };
      }
    }, []);

    return ref;
  };

  useSortChange = (onSortChange?: (items: ReactElement[]) => unknown) => {
    useSubscription({
      makeSubscription: () => {
        return this.#emitter.on('sort', () => {
          onSortChange?.([...this.#items]);
        });
      },
    });
  };

  useWrapperEvents = () => {
    const unsuscribe = useRef(() => {});
    const ref = useCallback((el: HTMLElement) => {
      unsuscribe.current();

      if (el) {
        const handleDrag = (ev: DragEvent) => {
          const target = getTargetItem(ev);

          if (ev.dataTransfer) {
            // eslint-disable-next-line no-param-reassign
            ev.dataTransfer.effectAllowed = 'all';
            ev.dataTransfer.setData('text', target.id);
            target.classList.add('dragging');
          }
        };

        el.addEventListener('dragstart', handleDrag);

        unsuscribe.current = () => {
          el.removeEventListener('dragstart', handleDrag);
        };
      }
    }, []);

    return ref;
  };
}

const con = createContext<SortableListHandler>({} as SortableListHandler);

export const SortableListContext = ({
  boxProps,
  children,
  onSortChange,
}: {
  boxProps?: BoxProps;
  children: ReactNode;
  onSortChange?: (items: ReactElement<TSortableListItem>[]) => unknown;
}) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handler = useMemo(() => new SortableListHandler(), []);
  handler.useSortChange(onSortChange);
  handler.updateChildren(children);

  return (
    <Box
      {...boxProps}
      as={boxProps?.as ?? 'ul'}
      className={`sortableList ${boxProps?.className ?? ''}`}
      ref={handler.useWrapperEvents()}
    >
      <con.Provider value={handler}>{children}</con.Provider>
    </Box>
  );
};

export function useSortableListHandler() {
  return useContext(con);
}

export type { TSortableListItem };
